/**
 *  @swagger
 *  definitions:
 *    ChampionshipSponsorParams:
 *      type: object
 *      properties:
 *        ChampionshipId:
 *          description: ID of the associated Championship
 *          type: string
 *        Name:
 *          description: Name of the ChampionshipSponsor
 *          type: string
 *        Link:
 *          description: Link of the ChampionshipSponsor
 *          type: string
 *        Image:
 *          description: Image of the ChampionshipSponsor
 *          type: string
 *
 *  @swagger
 *  definitions:
 *    ChampionshipSponsorCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/ChampionshipSponsorParams'
 *        - type: object
 *          required:
 *            - ChampionshipId
 *            - Name
 *            - Link
 *            - Image
 *
 *  @swagger
 *  definitions:
 *    ChampionshipSponsorQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the ChampionshipSponsor
 *              type: string
 *        - $ref: '#/definitions/ChampionshipSponsorParams'
 *
 *  @swagger
 *  definitions:
 *    ChampionshipSponsorUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/ChampionshipSponsorQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    ChampionshipSponsor:
 *      allOf:
 *        - $ref: '#/definitions/ChampionshipSponsorCreateParams'
 *        - $ref: '#/definitions/ChampionshipSponsorUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      ChampionshipSponsor:
 *        allOf:
 *          - $ref: '#/definitions/ChampionshipSponsor'
 *          - type: object
 *            properties:
 *              Championship:
 *                description: Championship of the ChampionshipSponsor
 *                $ref: '#/components/schemas/Championship'
 */

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $RelationIds = {
  ChampionshipId: string;
};

export type $Params = {
  Image: string;
  Link: string;
  Name: string;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  Championship?: $Model<'Championship'>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  Image: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Link: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Name: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'ChampionshipSponsor'> => {
  const ChampionshipSponsor = stack.define(
    'ChampionshipSponsor',
    fields(Sequelize),
    {
      tableName: 'ChampionshipSponsors',
    },
  );

  ChampionshipSponsor.associate = (models: $ModelClasses) => {
    models.ChampionshipSponsor.belongsTo(
      models.Championship,
      {
        as: 'Championship',
        foreignKey: 'ChampionshipId',
      },
    );
  };

  ChampionshipSponsor.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'ASC']],
    },
  );

  return ChampionshipSponsor;
});
